import { useEffect, useMemo, useState } from 'react'
import { BottomSheet } from './BottomSheet.jsx'
import { AnchoredPopover } from './AnchoredPopover.jsx'
import {
  todayLil, shiftLilMonth, formatLilMonthJsx, formatLilYearJsx,
  lilToHe, heToLil,
} from '../lil.jsx'
import { gregorianToPsc, pscToGregorian, pscObjectToString } from '@justhyur/lil-calendar'
import { useIsDesktop } from '../useIsDesktop.js'

// ── Helpers ─────────────────────────────────────────────────────────────
function isoToParts(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  return { year: y, month: m, day: d }
}
function partsToIso({ year, month, day }) {
  return `${year}-${pad(month)}-${pad(day)}`
}
function pad(n) { return String(n).padStart(2, '0') }

function shiftGregMonth(year, month, delta) {
  let m = month + delta
  let y = year
  while (m < 1)  { m += 12; y -= 1 }
  while (m > 12) { m -= 12; y += 1 }
  return { year: y, month: m }
}

function gregMonthCells(year, month) {
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7 // Mon=0
  const lastDay = new Date(year, month, 0).getDate()
  const cells = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= lastDay; d++) cells.push(d)
  while (cells.length % 7) cells.push(null)
  return cells
}

function gregMonthLabel(year, month, locale) {
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' })
    .format(new Date(year, month - 1, 1))
}

function gregWeekdayHeaders(locale) {
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  // Monday-first
  const fmt = new Intl.DateTimeFormat(lang, { weekday: 'narrow' })
  // Reference week starting Monday Jan 4, 1970 (Mon)
  return [0, 1, 2, 3, 4, 5, 6].map(i => {
    const d = new Date(1970, 0, 5 + i) // 5 Jan 1970 = Monday
    return fmt.format(d).toUpperCase()
  })
}

// Lil grid: 28 days for normal months (padded so day 1 lands on its actual
// Gregorian weekday), [DR?, PH] for the special slot.
function lilMonthCells({ year, month, special }) {
  if (special) {
    const cells = []
    try { pscToGregorian(`PSC${pscYearStr(year)}DR`); cells.push('DR') } catch {}
    cells.push('PH')
    return cells
  }
  const firstIso = pscToGregorian(pscObjectToString({ year, month, day: 1 }))
  const [fy, fm, fd] = firstIso.split('-').map(Number)
  const firstWeekday = (new Date(fy, fm - 1, fd).getDay() + 6) % 7 // Mon=0
  const cells = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= 28; d++) cells.push(d)
  while (cells.length % 7) cells.push(null)
  return cells
}

function pscYearStr(year) {
  return year >= 0
    ? String(year).padStart(5, '0')
    : '-' + String(Math.abs(year)).padStart(5, '0')
}

// ── Component ───────────────────────────────────────────────────────────
export function DatePicker({
  value,        // 'YYYY-MM-DD'
  onChange,     // (iso) => void
  onClose,
  open,
  isLilAvailable,
  yearEra,
  locale,
  t,
  anchorRef,    // optional: when provided + desktop, renders as anchored popover
}) {
  const isDesktop = useIsDesktop()
  // Default mode is always Greg; user taps Lil to switch (when available)
  const [mode, setMode] = useState('greg')

  // Greg view state (year+month)
  const initGreg = isoToParts(value || isoToday())
  const [gView, setGView] = useState({ year: initGreg.year, month: initGreg.month })

  // Lil view state (year+month or special)
  const valueLilPsc = useMemo(() => {
    try { return gregorianToPsc(new Date(value)) } catch { return null }
  }, [value])
  const initLil = valueLilPsc
    ? (typeof valueLilPsc.day === 'string'
        ? { year: valueLilPsc.year, special: true }
        : { year: valueLilPsc.year, month: valueLilPsc.month })
    : todayLil()
  const [lView, setLView] = useState(initLil)

  // Re-sync views when (re)opening
  useEffect(() => {
    if (!open) return
    const parts = isoToParts(value || isoToday())
    setGView({ year: parts.year, month: parts.month })
    try {
      const psc = gregorianToPsc(new Date(value || isoToday()))
      setLView(typeof psc.day === 'string'
        ? { year: psc.year, special: true }
        : { year: psc.year, month: psc.month })
    } catch {}
    setMode('greg')
  }, [open, value, isLilAvailable])

  function pickGreg(day) {
    if (!day) return
    onChange(partsToIso({ year: gView.year, month: gView.month, day }))
    onClose()
  }

  function pickLil(cell) {
    let iso
    if (typeof cell === 'string') {
      // 'DR' or 'PH'
      try {
        iso = pscToGregorian(`PSC${pscYearStr(lView.year)}${cell}`)
      } catch { return }
    } else {
      iso = pscToGregorian(pscObjectToString({
        year: lView.year, month: lView.month, day: cell,
      }))
    }
    onChange(iso)
    onClose()
  }

  function resetToToday() {
    onChange(isoToday())
    onClose()
  }

  const content = (
    <>
      <div className="dp-header">
        <span className="dp-title">{t.transactions.date}</span>
        {isLilAvailable && (
          <div className="dp-mode-toggle" role="tablist">
            <button
              type="button"
              className={`dp-mode-btn${mode === 'lil' ? ' active' : ''}`}
              onClick={() => setMode('lil')}
            >Lil</button>
            <button
              type="button"
              className={`dp-mode-btn${mode === 'greg' ? ' active' : ''}`}
              onClick={() => setMode('greg')}
            >Greg</button>
          </div>
        )}
        <button type="button" className="dp-reset" onClick={resetToToday}>
          {t.transactions.today}
        </button>
        <button type="button" className="dp-close" onClick={onClose} aria-label={t.common.close}>
          <CloseIcon />
        </button>
      </div>

      {mode === 'greg'
        ? <GregView gView={gView} setGView={setGView} value={value} pick={pickGreg} locale={locale} />
        : <LilView  lView={lView} setLView={setLView} value={value} pick={pickLil}  yearEra={yearEra} locale={locale} />
      }
    </>
  )

  // Desktop + anchor available → compact anchored popover, no bottom-sheet
  if (isDesktop && anchorRef) {
    return (
      <AnchoredPopover open={open} onClose={onClose} anchorRef={anchorRef} width={320} className="dp-popover">
        {content}
      </AnchoredPopover>
    )
  }
  return (
    <BottomSheet open={open} onClose={onClose} className="dp-sheet">
      {content}
    </BottomSheet>
  )
}

function GregView({ gView, setGView, value, pick, locale }) {
  const [yearView, setYearView] = useState(false)
  const [yearBase, setYearBase] = useState(gView.year - 6)
  const cells = useMemo(() => gregMonthCells(gView.year, gView.month), [gView])
  const weekdays = useMemo(() => gregWeekdayHeaders(locale), [locale])
  const today = isoToday()
  const todayYear = Number(today.split('-')[0])

  if (yearView) {
    return (
      <YearGrid
        base={yearBase}
        onPrev={() => setYearBase(b => b - 12)}
        onNext={() => setYearBase(b => b + 12)}
        onLabel={() => setYearView(false)}
        renderYear={(y) => y}
        isSelected={(y) => y === gView.year}
        isToday={(y) => y === todayYear}
        onPick={(y) => { setGView(v => ({ ...v, year: y })); setYearView(false) }}
      />
    )
  }
  return (
    <>
      <MonthNav
        label={gregMonthLabel(gView.year, gView.month, locale)}
        onPrev={() => setGView(shiftGregMonth(gView.year, gView.month, -1))}
        onNext={() => setGView(shiftGregMonth(gView.year, gView.month, +1))}
        onLabel={() => { setYearBase(gView.year - 6); setYearView(true) }}
      />
      <div className="dp-weekdays">
        {weekdays.map((w, i) => <span key={i} className="dp-wkd">{w}</span>)}
      </div>
      <div className="dp-grid">
        {cells.map((d, i) => {
          if (d == null) return <span key={i} className="dp-cell empty" />
          const iso = partsToIso({ year: gView.year, month: gView.month, day: d })
          const selected = value === iso
          const isToday  = today === iso
          return (
            <button
              key={i}
              type="button"
              className={`dp-cell${selected ? ' selected' : ''}${isToday ? ' today' : ''}`}
              onClick={() => pick(d)}
            >{d}</button>
          )
        })}
      </div>
    </>
  )
}

function LilView({ lView, setLView, value, pick, yearEra, locale }) {
  const [yearView, setYearView] = useState(false)
  // Year selection works in the displayed era (HE or Lil) and converts on pick.
  const dispYear = yearEra === 'he' ? lilToHe(lView.year) : lView.year
  const [yearBase, setYearBase] = useState(dispYear - 6)
  const cells = useMemo(() => lilMonthCells(lView), [lView])
  const weekdays = useMemo(() => gregWeekdayHeaders(locale), [locale])
  const todayIso = isoToday()
  const todayLilYear = todayLil().year
  const dispTodayYear = yearEra === 'he' ? lilToHe(todayLilYear) : todayLilYear

  if (yearView) {
    return (
      <YearGrid
        base={yearBase}
        onPrev={() => setYearBase(b => b - 12)}
        onNext={() => setYearBase(b => b + 12)}
        onLabel={() => setYearView(false)}
        renderYear={(y) => y}
        isSelected={(y) => y === dispYear}
        isToday={(y) => y === dispTodayYear}
        onPick={(y) => {
          const lilY = yearEra === 'he' ? heToLil(y) : y
          setLView(v => ({ ...v, year: lilY }))
          setYearView(false)
        }}
      />
    )
  }
  return (
    <>
      <MonthNav
        label={
          lView.special
            ? <>{formatLilYearJsx(lView.year, yearEra)} – {formatLilYearJsx(lView.year + 1, yearEra)}</>
            : formatLilMonthJsx(lView, yearEra)
        }
        onPrev={() => setLView(shiftLilMonth(lView, -1))}
        onNext={() => setLView(shiftLilMonth(lView, +1))}
        onLabel={() => { setYearBase(dispYear - 6); setYearView(true) }}
      />
      {!lView.special && (
        <div className="dp-weekdays">
          {weekdays.map((w, i) => <span key={i} className="dp-wkd">{w}</span>)}
        </div>
      )}
      <div className={`dp-grid${lView.special ? ' dp-grid-sp' : ''}`}>
        {cells.map((cell, i) => {
          if (cell == null) return <span key={i} className="dp-cell empty" />
          let iso = null
          try {
            iso = typeof cell === 'string'
              ? pscToGregorian(`PSC${pscYearStr(lView.year)}${cell}`)
              : pscToGregorian(pscObjectToString({ year: lView.year, month: lView.month, day: cell }))
          } catch {}
          const selected = iso && value === iso
          const isToday  = iso && todayIso === iso
          return (
            <button
              key={i}
              type="button"
              className={`dp-cell${selected ? ' selected' : ''}${isToday ? ' today' : ''}`}
              onClick={() => pick(cell)}
            >{cell}</button>
          )
        })}
      </div>
    </>
  )
}

function MonthNav({ label, onPrev, onNext, onLabel }) {
  return (
    <div className="dp-month-nav">
      <button type="button" className="dp-arrow" onClick={onPrev} aria-label="Previous">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      {onLabel
        ? <button type="button" className="dp-month-label dp-month-label-btn" onClick={onLabel}>{label}</button>
        : <span className="dp-month-label">{label}</span>}
      <button type="button" className="dp-arrow" onClick={onNext} aria-label="Next">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  )
}

// Year-selection grid — opened by clicking the month/year label. `base` is the
// first of 12 displayed years; arrows page by 12; the label returns to months.
function YearGrid({ base, onPrev, onNext, onLabel, renderYear, isSelected, isToday, onPick }) {
  const years = Array.from({ length: 12 }, (_, i) => base + i)
  return (
    <>
      <div className="dp-month-nav">
        <button type="button" className="dp-arrow" onClick={onPrev} aria-label="Previous">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button type="button" className="dp-month-label dp-month-label-btn" onClick={onLabel}>
          {base}–{base + 11}
        </button>
        <button type="button" className="dp-arrow" onClick={onNext} aria-label="Next">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
      <div className="dp-year-grid">
        {years.map(y => (
          <button
            key={y}
            type="button"
            className={`dp-year${isSelected(y) ? ' selected' : ''}${isToday(y) ? ' today' : ''}`}
            onClick={() => onPick(y)}
          >{renderYear(y)}</button>
        ))}
      </div>
    </>
  )
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function isoToday() {
  const d = new Date()
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
